import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Paper,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAuth } from "../../contexts/AuthContext";
import { useDock } from "../../contexts/DockContext";
import { useDrawer } from "../../contexts/DrawerContext";
import { useDisplayMode } from "../../contexts/DisplayModeContext";
import { useLinkOrder } from "../../contexts/LinkOrderContext";
import userManagementApi from "../../services/user-management-api";
import CalendarPage from "../Calendar";
import AnnouncementsPanel from "../../components/Home/AnnouncementsPanel";
import externalLinksApiService, {
  ExternalLink,
} from "../../services/external-links-api";
import {
  parseExternalLinkDescription,
  getInitialLetter,
} from "../../utils/externalLinkLogoHelper";
import Dock, { DockItemData } from "../../components/Dock";
import Drawer from "../../components/Drawer";
import {
  useEasterEggs,
  EasterEggState,
} from "../../hooks/useEasterEggs";
import GravityMode from "../../components/EasterEggs/GravityMode";
import MatrixRain from "../../components/EasterEggs/MatrixRain";
import DiscoMode from "../../components/EasterEggs/DiscoMode";
import BrickBreaker from "../../components/EasterEggs/BrickBreaker";
import ClickAMole from "../../components/EasterEggs/ClickAMole";

// Sortable card link component for Card Mode drag-and-drop
interface SortableCardLinkProps {
  link: ExternalLink;
  imageErrors: Set<string>;
  onImageError: (linkId: string) => void;
  t: (key: string) => string;
}

const SortableCardLink: React.FC<SortableCardLinkProps> = ({
  link,
  imageErrors,
  onImageError,
  t,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id?.toString() || "" });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  const { logo, description } = parseExternalLinkDescription(link.description);
  const linkId = link.id?.toString() || "";
  const hasImageError = imageErrors.has(linkId);
  const shouldShowImage = logo && !hasImageError;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.75,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)"
            : "linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(33, 150, 243, 0.02) 100%)",
        border: 1,
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.1)"
            : "rgba(33, 150, 243, 0.15)",
        borderRadius: 1.5,
        "&:hover": {
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(33, 150, 243, 0.2) 0%, rgba(33, 150, 243, 0.1) 100%)"
              : "linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(33, 150, 243, 0.06) 100%)",
          borderColor: "primary.main",
          transform: "translateX(4px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 4px 12px rgba(33, 150, 243, 0.2)"
              : "0 4px 12px rgba(33, 150, 243, 0.15)",
        },
      }}
      onClick={() => window.open(link.link, "_blank")}
    >
      {/* Logo Avatar */}
      <Avatar
        src={
          shouldShowImage
            ? logo.type === "url"
              ? logo.data
              : logo.data
            : undefined
        }
        imgProps={{
          onError: () => onImageError(linkId),
        }}
        sx={{
          width: 40,
          height: 40,
          mr: 1.5,
          bgcolor: shouldShowImage ? "transparent" : "primary.main",
          fontSize: "1.2rem",
          fontWeight: 600,
        }}
      >
        {!shouldShowImage && getInitialLetter(link.title)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "text.primary",
          }}
        >
          {link.title}
        </Typography>
        {description && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "text.secondary",
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
      <Button
        size="small"
        variant="text"
        endIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem" }} />}
        onClick={(e) => {
          e.stopPropagation();
          window.open(link.link, "_blank");
        }}
        sx={{
          ml: 1.5,
          flexShrink: 0,
          color: "primary.main",
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          "&:hover": {
            backgroundColor: "transparent",
            color: "primary.dark",
          },
        }}
      >
        {t("common.open")}
      </Button>
    </Paper>
  );
};

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const { dockPosition } = useDock();
  const { drawerPosition } = useDrawer();
  const { displayMode } = useDisplayMode();
  const { linkOrder, setLinkOrder } = useLinkOrder();
  const [externalLinks, setExternalLinks] = useState<ExternalLink[]>([]);
  const [firstName, setFirstName] = useState<string>(t("common.user"));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [announcementCount, setAnnouncementCount] = useState<number | null>(
    null,
  );

  // ── Easter Egg state ──
  const homeRef = useRef<HTMLDivElement>(null);
  const [easterEgg, setEasterEgg] = useState<EasterEggState>({ type: "none" });

  useEasterEggs({
    active: easterEgg,
    onActivate: (state) => {
      try {
        setEasterEgg(state);
      } catch (e) {
        console.error("Easter egg activation error:", e);
      }
    },
    onDeactivate: () => setEasterEgg({ type: "none" }),
  });

  // On mobile, always use card mode
  const effectiveDisplayMode = isMobile ? "card" : displayMode;

  // Hide announcements panel when there are no active announcements
  const hasAnnouncements = announcementCount !== null && announcementCount > 0;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch full profile to get first name
      if (user?.id) {
        const profile = await userManagementApi.getMyProfile();
        setFirstName(profile.firstName || t("common.user"));
      }

      // Fetch external links
      const links = await externalLinksApiService.getAllActiveExternalLinks();
      setExternalLinks(links);
    } catch (err) {
      console.error(t("error.errorLoadingData"), err);
      setError(
        err instanceof Error ? err.message : t("error.failedToLoadData"),
      );
    } finally {
      setLoading(false);
    }
  };

  const groupedLinks = externalLinks.reduce(
    (acc, link) => {
      if (!acc[link.group]) {
        acc[link.group] = [];
      }
      acc[link.group].push(link);
      return acc;
    },
    {} as Record<string, ExternalLink[]>,
  );

  const handleImageError = (linkId: string) => {
    setImageErrors((prev) => new Set(prev).add(linkId));
  };

  // Prepare dock items from external links (Services, Development, Management)
  const dockItems: DockItemData[] = useMemo(() => {
    const targetGroups = ["Services", "Development", "Management"];
    const items: DockItemData[] = [];

    targetGroups.forEach((group) => {
      const groupLinks = groupedLinks[group] || [];
      groupLinks.forEach((link) => {
        const { logo } = parseExternalLinkDescription(link.description);
        const linkId = link.id?.toString() || "";
        const hasImageError = imageErrors.has(linkId);
        const shouldShowImage = logo && !hasImageError;

        // Create icon element
        const icon = shouldShowImage ? (
          <Avatar
            src={logo.type === "url" ? logo.data : logo.data}
            imgProps={{
              onError: () => handleImageError(linkId),
            }}
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "12px",
            }}
          >
            {getInitialLetter(link.title)}
          </Avatar>
        ) : (
          <Avatar
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: "primary.main",
              fontSize: "1.5rem",
              fontWeight: 600,
              borderRadius: "12px",
            }}
          >
            {getInitialLetter(link.title)}
          </Avatar>
        );

        items.push({
          id: linkId,
          label: link.title,
          icon: icon,
          isRunning: false,
          onClick: () => window.open(link.link, "_blank"),
        });
      });
    });

    // Apply saved link order
    if (linkOrder && linkOrder.length > 0) {
      const itemMap = new Map(items.map((item) => [item.id, item]));
      const ordered: DockItemData[] = [];

      for (const id of linkOrder) {
        const item = itemMap.get(id);
        if (item) {
          ordered.push(item);
          itemMap.delete(id);
        }
      }

      // Append any new items not in the saved order
      for (const item of itemMap.values()) {
        ordered.push(item);
      }

      return ordered;
    }

    return items;
  }, [externalLinks, imageErrors, linkOrder]);

  const handleReorder = (reorderedItems: DockItemData[]) => {
    setLinkOrder(reorderedItems.map((item) => item.id));
  };

  // DnD sensors for card mode
  const cardSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Get ordered links for a group (applying user's saved order)
  const getOrderedGroupLinks = (group: string): ExternalLink[] => {
    const groupLinks = groupedLinks[group] || [];
    if (!linkOrder || linkOrder.length === 0) {
      return groupLinks;
    }

    const linkMap = new Map(
      groupLinks.map((link) => [link.id?.toString() || "", link]),
    );
    const ordered: ExternalLink[] = [];

    for (const id of linkOrder) {
      const link = linkMap.get(id);
      if (link) {
        ordered.push(link);
        linkMap.delete(id);
      }
    }

    // Append any links not in the saved order
    for (const link of linkMap.values()) {
      ordered.push(link);
    }

    return ordered;
  };

  const handleCardDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Get all link IDs in current display order across all groups
      const targetGroups = ["Services", "Development", "Management"];
      const allOrderedIds: string[] = [];

      targetGroups.forEach((group) => {
        const orderedLinks = getOrderedGroupLinks(group);
        orderedLinks.forEach((link) => {
          allOrderedIds.push(link.id?.toString() || "");
        });
      });

      const oldIndex = allOrderedIds.indexOf(active.id as string);
      const newIndex = allOrderedIds.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(allOrderedIds, oldIndex, newIndex);
        setLinkOrder(newOrder);
      }
    }
  };

  const renderLinkGroup = (group: string, isHalfWidth: boolean = false) => {
    const orderedLinks = getOrderedGroupLinks(group);

    return (
      <Box key={group}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: "primary.main",
            textTransform: "uppercase",
            fontSize: "0.75rem",
            letterSpacing: 1,
          }}
        >
          {group}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isHalfWidth
              ? {
                  xs: "1fr",
                  sm: "1fr",
                  md: "1fr",
                  lg: "1fr 1fr",
                }
              : {
                  xs: "1fr",
                  sm: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr 1fr",
                },
            gap: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          <DndContext
            sensors={cardSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleCardDragEnd}
          >
            <SortableContext
              items={orderedLinks.map((link) => link.id?.toString() || "")}
              strategy={rectSortingStrategy}
            >
              {orderedLinks.map((link) => (
                <SortableCardLink
                  key={link.id}
                  link={link}
                  imageErrors={imageErrors}
                  onImageError={handleImageError}
                  t={t}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      </Box>
    );
  };

  // Check if external links section will be shown
  const showExternalLinks = effectiveDisplayMode === "card";

  // Calculate content height: viewport - navbar(64px) - welcome header(~50px) - paddings(~24px)
  const contentHeight = "calc(100vh - 138px)";

  return (
    <Box
      ref={homeRef}
      sx={{
        px: 2,
        pb: showExternalLinks ? 2 : 0,
        width: "100%",
        height: showExternalLinks ? "auto" : "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        overflow: showExternalLinks ? "visible" : "hidden",
        position: "relative",
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontWeight: 800,
            mb: 0.5,
          }}
        >
          {t("home.welcome")}, {firstName}!
        </Typography>
      </Box>

      {/* Calendar and Announcements Row */}
      <Grid
        container
        spacing={1}
        sx={{
          mb: showExternalLinks ? 1 : 0,
          flex: showExternalLinks ? "0 0 auto" : 1,
          minHeight: 0, // Important for flex children to shrink
        }}
      >
        {/* Announcements Section - 30% width on desktop, hidden when no announcements */}
        {hasAnnouncements && (
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{ height: showExternalLinks ? "auto" : "100%" }}
          >
            <AnnouncementsPanel
              maxHeight={showExternalLinks ? 500 : contentHeight}
              onAnnouncementsLoaded={setAnnouncementCount}
            />
          </Grid>
        )}
        {/* Render hidden panel to load announcements count if not yet known */}
        {announcementCount === null && (
          <Box sx={{ display: "none" }}>
            <AnnouncementsPanel onAnnouncementsLoaded={setAnnouncementCount} />
          </Box>
        )}

        {/* Calendar Section - Full width when no announcements, 70% otherwise */}
        <Grid
          size={{ xs: 12, md: hasAnnouncements ? 8 : 12 }}
          sx={{ height: showExternalLinks ? "auto" : "100%" }}
        >
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: 1,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(33, 150, 243, 0.1)",
              borderRadius: 2,
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                  : "0 8px 32px rgba(33, 150, 243, 0.1)",
            }}
          >
            <CardContent
              sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column" }}
            >
              <Box
                sx={{
                  p: 2.5,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CalendarPage
                  compact={showExternalLinks}
                  fillHeight={!showExternalLinks}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* External Links - Only show in Card Mode */}
      {effectiveDisplayMode === "card" && (
        <>
          {/* Services Section - Full Width */}
          <Card
            sx={{
              width: "100%",
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: 1,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(33, 150, 243, 0.1)",
              borderRadius: 2,
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                  : "0 8px 32px rgba(33, 150, 243, 0.1)",
              mb: 1,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <Stack spacing={2}>{renderLinkGroup("Services")}</Stack>
              )}
            </CardContent>
          </Card>

          {/* Row: Development (50%) and Management (50%) */}
          <Grid container spacing={1} sx={{ width: "100%" }}>
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: 1,
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(33, 150, 243, 0.1)",
                  borderRadius: 2,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                      : "0 8px 32px rgba(33, 150, 243, 0.1)",
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 3 }}
                    >
                      <CircularProgress size={40} />
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {renderLinkGroup("Development", true)}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
              <Card
                sx={{
                  height: "100%",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  border: 1,
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(33, 150, 243, 0.1)",
                  borderRadius: 2,
                  boxShadow: (theme) =>
                    theme.palette.mode === "dark"
                      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
                      : "0 8px 32px rgba(33, 150, 243, 0.1)",
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 3 }}
                    >
                      <CircularProgress size={40} />
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {renderLinkGroup("Management", true)}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* No Links Message - Only show in Card Mode */}
      {showExternalLinks && externalLinks.length === 0 && !loading && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">{t("home.noLinks")}</Typography>
        </Box>
      )}

      {/* Dock Component - Only show in Dock Mode and not on mobile */}
      {effectiveDisplayMode === "dock" &&
        !loading &&
        dockItems.length > 0 &&
        !isMobile && (
          <Dock
            items={dockItems}
            position={dockPosition}
            onReorder={handleReorder}
          />
        )}

      {/* Drawer Component - Only show in Drawer Mode and not on mobile */}
      {effectiveDisplayMode === "drawer" &&
        !loading &&
        dockItems.length > 0 &&
        !isMobile && (
          <Drawer
            items={dockItems}
            position={drawerPosition}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onOpen={() => setDrawerOpen(true)}
            onReorder={handleReorder}
          />
        )}

      {/* ── Easter Egg Effects ── */}
      {easterEgg.type === "visual" && easterEgg.effect === "matrix" && (
        <MatrixRain onEnd={() => setEasterEgg({ type: "none" })} />
      )}
      {easterEgg.type === "visual" && easterEgg.effect === "gravity" && (
        <GravityMode
          containerRef={homeRef}
          onEnd={() => setEasterEgg({ type: "none" })}
        />
      )}
      {easterEgg.type === "visual" && easterEgg.effect === "disco" && (
        <DiscoMode
          containerRef={homeRef}
          onEnd={() => setEasterEgg({ type: "none" })}
        />
      )}
      {easterEgg.type === "game" && easterEgg.game === "brickbreaker" && (
        <BrickBreaker
          containerRef={homeRef}
          onEnd={() => setEasterEgg({ type: "none" })}
        />
      )}
      {easterEgg.type === "game" && easterEgg.game === "clickamole" && (
        <ClickAMole
          containerRef={homeRef}
          onEnd={() => setEasterEgg({ type: "none" })}
        />
      )}
    </Box>
  );
};

export default HomePage;
